import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Paper,
  Button,
  Link
} from '@material-ui/core';
import { Refresh as RefreshIcon, OpenInNew as OpenInNewIcon } from '@material-ui/icons';
import { getFirestore, doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { 
  getAnalytics, 
  logEvent, 
  getAnalytics as getFirebaseAnalytics,
} from 'firebase/analytics';
import { formatDistanceToNow, subDays, format } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer
} from 'recharts';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    padding: '20px',
    marginTop: '20px'
  },
  headerSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px'
  },
  refreshButton: {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginLeft: '8px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)'
    }
  },
  statsCard: {
    backgroundColor: 'rgba(217, 217, 217, 0.3)',
    color: 'white',
    height: '100%',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.02)'
    }
  },
  statsValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '8px'
  },
  statsLabel: {
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  subValue: {
    fontSize: '1.1rem',
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: '8px'
  },
  tabPanel: {
    padding: theme.spacing(3)
  },
  chartContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    height: '400px'
  },
  analyticsLinks: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  externalLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'white',
    textDecoration: 'none',
    padding: '10px 16px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '&.firebase': {
      backgroundColor: '#1A73E8',
      '&:hover': {
        backgroundColor: '#1557B0'
      }
    },
    '&.google': {
      backgroundColor: '#EA4335',
      '&:hover': {
        backgroundColor: '#B31412'
      }
    }
  },
  analyticsCard: {
    backgroundColor: 'rgba(217, 217, 217, 0.3)',
    color: 'white',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3)
  },
  analyticsCardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    '& h6': {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: 'rgba(255, 255, 255, 0.9)'
    }
  },
  analyticsMetric: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  metricLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.95rem'
  },
  metricValue: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: 'rgba(255, 255, 255, 0.95)'
  }
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export function AnalyticsPanel() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsersLast30Days: 0,
    totalRevenue: 0,
    revenueLast30Days: 0,
    totalPurchases: 0,
    purchasesLast30Days: 0,
    mostPopularCourse: { name: '', purchases: 0 },
    averageCompletionRate: 0,
    lastPurchase: null,
    activeSubscriptions: 0
  });
  const [analyticsData, setAnalyticsData] = useState({
    pageViews: [],
    events: [],
    userEngagement: [],
    loading: true,
    error: null
  });

  const FIREBASE_ANALYTICS_URL = "https://console.firebase.google.com/u/1/project/project-hector-6eaf1/analytics/app/web:ZjhiYzZlYjYtZDM4Ny00NTkwLTk0NzAtOTkxNWQyMzU3YTA2/overview/reports~2Fdashboard";
  const GOOGLE_ANALYTICS_URL = "https://analytics.google.com/analytics/web/?authuser=1&hl=en#/p364220786/realtime/overview";

  useEffect(() => {
    fetchSiteStats();
    fetchAnalyticsData();
  }, []);

  const fetchSiteStats = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      // Get users data
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt ? 
          (typeof doc.data().createdAt === 'string' ? 
            new Date(doc.data().createdAt) : 
            doc.data().createdAt.toDate()
          ) : null
      }));

      // Calculate user stats
      const totalUsers = users.length;
      const newUsersLast30Days = users.filter(user => 
        user.createdAt && user.createdAt > thirtyDaysAgo
      ).length;

      // Calculate revenue and purchase stats
      let totalRevenue = 0;
      let revenueLast30Days = 0;
      let totalPurchases = 0;
      let purchasesLast30Days = 0;
      let coursePurchases = {};
      let lastPurchaseDate = null;
      let completedCourses = 0;
      let totalCourseEnrollments = 0;

      users.forEach(user => {
        if (user.productsOwned) {
          user.productsOwned.forEach(product => {
            totalPurchases++;
            totalRevenue += product.price || 0;
            
            // Track course popularity
            coursePurchases[product.name] = (coursePurchases[product.name] || 0) + 1;
            
            // Check if purchase was in last 30 days
            const purchaseDate = new Date(product.createdAt);
            if (purchaseDate > thirtyDaysAgo) {
              purchasesLast30Days++;
              revenueLast30Days += product.price || 0;
            }
            
            // Track last purchase
            if (!lastPurchaseDate || purchaseDate > lastPurchaseDate) {
              lastPurchaseDate = purchaseDate;
            }
          });
        }

        // Track course completion stats
        if (user.completedCourses) {
          completedCourses += user.completedCourses.length;
        }
        if (user.productsOwned) {
          totalCourseEnrollments += user.productsOwned.length;
        }
      });

      // Find most popular course
      const mostPopularCourse = Object.entries(coursePurchases)
        .reduce((max, [name, purchases]) => 
          purchases > (max.purchases || 0) ? { name, purchases } : max
        , { name: '', purchases: 0 });

      // Calculate completion rate
      const averageCompletionRate = totalCourseEnrollments > 0 
        ? (completedCourses / totalCourseEnrollments) * 100 
        : 0;

      setStats({
        totalUsers,
        newUsersLast30Days,
        totalRevenue,
        revenueLast30Days,
        totalPurchases,
        purchasesLast30Days,
        mostPopularCourse,
        averageCompletionRate,
        lastPurchase: lastPurchaseDate
      });
    } catch (error) {
      console.error('Error fetching site stats:', error);
      toast.error('Error fetching site statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setAnalyticsData(prev => ({ ...prev, loading: true, error: null }));
      
      // Initialize Firebase Analytics
      const analytics = getFirebaseAnalytics();
      
      // Get the date range for the last 30 days
      const endDate = new Date();
      const startDate = subDays(endDate, 30);
      
      // Create an array of dates for the last 30 days
      const dates = Array.from({ length: 31 }, (_, i) => {
        const date = subDays(endDate, i);
        return format(date, 'yyyy-MM-dd');
      }).reverse();

      // Get analytics data from Firebase
      const analyticsRef = collection(getFirestore(), 'analytics_events');
      const analyticsQuery = query(
        analyticsRef,
        where('date', '>=', startDate.toISOString()),
        where('date', '<=', endDate.toISOString())
      );

      const analyticsSnapshot = await getDocs(analyticsQuery);
      
      // Process the analytics data
      const processedData = {
        pageViews: {},
        events: {},
        userEngagement: {}
      };

      // Initialize data structure with zeros for all dates
      dates.forEach(date => {
        processedData.pageViews[date] = 0;
        processedData.events[date] = 0;
        processedData.userEngagement[date] = 0;
      });

      // Process the analytics data
      analyticsSnapshot.forEach(doc => {
        const data = doc.data();
        const date = format(new Date(data.date), 'yyyy-MM-dd');

        if (data.type === 'page_view') {
          processedData.pageViews[date] = (processedData.pageViews[date] || 0) + 1;
        } else if (data.type === 'user_engagement') {
          processedData.userEngagement[date] = (processedData.userEngagement[date] || 0) + (data.engagement_time || 0);
        }
        
        // Count all events
        processedData.events[date] = (processedData.events[date] || 0) + 1;
      });

      // Convert the processed data into arrays for the charts
      const chartData = {
        pageViews: dates.map(date => ({
          date,
          views: processedData.pageViews[date] || 0
        })),
        events: dates.map(date => ({
          date,
          count: processedData.events[date] || 0
        })),
        userEngagement: dates.map(date => ({
          date,
          duration: Math.round(processedData.userEngagement[date] / 60) || 0 // Convert to minutes
        }))
      };

      setAnalyticsData({
        ...chartData,
        loading: false,
        error: null
      });

      // Log that we're viewing analytics
      logEvent(analytics, 'view_analytics', {
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setAnalyticsData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
      toast.error('Error fetching analytics data');
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.headerSection}>
        <Typography variant="h6" style={{ color: 'white' }}>
          Analytics Dashboard
        </Typography>
        <div className={classes.analyticsLinks}>
          <Link 
            href={FIREBASE_ANALYTICS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`${classes.externalLink} firebase`}
          >
            <OpenInNewIcon fontSize="small" />
            Firebase Analytics
          </Link>
          <Link 
            href={GOOGLE_ANALYTICS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`${classes.externalLink} google`}
          >
            <OpenInNewIcon fontSize="small" />
            Google Analytics
          </Link>
          <Tooltip title="Refresh Data">
            <IconButton 
              className={classes.refreshButton} 
              onClick={() => {
                fetchSiteStats();
                fetchAnalyticsData();
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <Paper style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Overview" style={{ color: 'white' }} />
          <Tab label="Traffic Analytics" style={{ color: 'white' }} />
          <Tab label="User Analytics" style={{ color: 'white' }} />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0} className={classes.tabPanel}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Total Revenue (30 Days)</Typography>
                <Typography className={classes.statsValue}>
                  {formatCurrency(stats.revenueLast30Days)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Total Users</Typography>
                <Typography className={classes.statsValue}>
                  {stats.totalUsers}
                </Typography>
                <Typography className={classes.subValue}>
                  New in 30 days: {stats.newUsersLast30Days}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Course Purchases</Typography>
                <Typography className={classes.statsValue}>
                  {stats.totalPurchases}
                </Typography>
                <Typography className={classes.subValue}>
                  Last 30 days: {stats.purchasesLast30Days}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Most Popular Course</Typography>
                <Typography className={classes.statsValue}>
                  {stats.mostPopularCourse.purchases}
                </Typography>
                <Typography className={classes.subValue}>
                  {stats.mostPopularCourse.name || 'No purchases yet'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Completion Rate</Typography>
                <Typography className={classes.statsValue}>
                  {stats.averageCompletionRate.toFixed(1)}%
                </Typography>
                <Typography className={classes.subValue}>
                  Average across all courses
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Latest Activity</Typography>
                <Typography className={classes.statsValue}>
                  {stats.lastPurchase 
                    ? formatDistanceToNow(stats.lastPurchase, { addSuffix: true })
                    : 'No purchases yet'
                  }
                </Typography>
                <Typography className={classes.subValue}>
                  Last course purchase
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <div className={classes.analyticsCard}>
          <div className={classes.analyticsCardHeader}>
            <Typography variant="h6">Quick Stats (Last 30 Days)</Typography>
          </div>
          <div className={classes.analyticsMetric}>
            <Typography className={classes.metricLabel}>Total Page Views</Typography>
            <Typography className={classes.metricValue}>
              {analyticsData.pageViews?.reduce((sum, day) => sum + day.views, 0)?.toLocaleString() || 0}
            </Typography>
          </div>
          <div className={classes.analyticsMetric}>
            <Typography className={classes.metricLabel}>Total Events</Typography>
            <Typography className={classes.metricValue}>
              {analyticsData.events?.reduce((sum, day) => sum + day.count, 0)?.toLocaleString() || 0}
            </Typography>
          </div>
          <div className={classes.analyticsMetric}>
            <Typography className={classes.metricLabel}>Avg. Daily Engagement</Typography>
            <Typography className={classes.metricValue}>
              {analyticsData.userEngagement?.length > 0
                ? `${Math.round(
                    analyticsData.userEngagement.reduce((sum, day) => sum + day.duration, 0) / 
                    analyticsData.userEngagement.length
                  ).toLocaleString()} min`
                : '0 min'
              }
            </Typography>
          </div>
        </div>
      </TabPanel>

      <TabPanel value={currentTab} index={1} className={classes.tabPanel}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" style={{ color: 'white', marginBottom: '20px' }}>
              Page Views (Last 30 Days)
            </Typography>
            <div className={classes.chartContainer}>
              {analyticsData.loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </div>
              ) : analyticsData.error ? (
                <Typography style={{ color: 'red', textAlign: 'center' }}>
                  Error loading analytics data: {analyticsData.error}
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.pageViews}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      stroke="white"
                      tickFormatter={(value) => format(new Date(value), 'MMM d')}
                    />
                    <YAxis stroke="white" />
                    <ChartTooltip
                      contentStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', border: 'none' }}
                      labelStyle={{ color: 'white' }}
                      formatter={(value) => [`${value} views`, 'Page Views']}
                      labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="views" 
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" style={{ color: 'white', marginBottom: '20px' }}>
              Events (Last 30 Days)
            </Typography>
            <div className={classes.chartContainer}>
              {analyticsData.loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </div>
              ) : analyticsData.error ? (
                <Typography style={{ color: 'red', textAlign: 'center' }}>
                  Error loading analytics data: {analyticsData.error}
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.events}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      stroke="white"
                      tickFormatter={(value) => format(new Date(value), 'MMM d')}
                    />
                    <YAxis stroke="white" />
                    <ChartTooltip
                      contentStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', border: 'none' }}
                      labelStyle={{ color: 'white' }}
                      formatter={(value) => [`${value} events`, 'Total Events']}
                      labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#82ca9d"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={2} className={classes.tabPanel}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" style={{ color: 'white', marginBottom: '20px' }}>
              User Engagement (Minutes per Day)
            </Typography>
            <div className={classes.chartContainer}>
              {analyticsData.loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </div>
              ) : analyticsData.error ? (
                <Typography style={{ color: 'red', textAlign: 'center' }}>
                  Error loading analytics data: {analyticsData.error}
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.userEngagement}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      stroke="white"
                      tickFormatter={(value) => format(new Date(value), 'MMM d')}
                    />
                    <YAxis stroke="white" />
                    <ChartTooltip
                      contentStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', border: 'none' }}
                      labelStyle={{ color: 'white' }}
                      formatter={(value) => [`${value} minutes`, 'Engagement Time']}
                      labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="duration" 
                      stroke="#ffc658"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Grid>
        </Grid>
      </TabPanel>
    </div>
  );
} 